import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChartLineUp, Lightning, Wrench, Buildings, Storefront, Factory,
  Users, ArrowUp, ArrowDown, Minus, Spinner, MagnifyingGlass, Info,
  Stack, Package, ShieldWarning, ChartBar, ArrowsClockwise, Siren, PaperPlaneTilt,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

const DIMENSIONS = [
  { key: "by_product_type", label: "Product type", icon: Wrench, hint: "rotary screw, piston, scroll, …" },
  { key: "by_product_model", label: "Model", icon: Stack, hint: "USCP25d1, US20/d2, …" },
  { key: "by_supplier", label: "Supplier", icon: Factory, hint: "primary supplier of PM kits on the unit" },
  { key: "by_dealer", label: "Dealer", icon: Storefront, hint: "ResellerID → company" },
  { key: "by_customer", label: "Customer", icon: Users, hint: "compressor owner" },
];

/**
 * Warranty Insights — admin dashboard.
 *
 * Lays out:
 *  • Window comparison strip (1m / 3m / 9m / 12m / 24m totals + emergency rate)
 *  • Tabbed dimension table (top 25 rows, count per window, delta vs prior 1m)
 *  • Recurring AI-cause clusters (count + example tickets + top products/suppliers)
 *
 * Backend: GET /api/warranty/insights[?windows=…]
 */
export default function WarrantyInsightsPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dim, setDim] = useState("by_product_type");
  const [search, setSearch] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [spikes, setSpikes] = useState(null);
  const [dispatching, setDispatching] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/warranty/insights", { params: { windows: "1,3,9,12,24" } })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
    api.get("/warranty/insights/spikes")
      .then((r) => setSpikes(r.data))
      .catch(() => setSpikes(null));
  }, [refreshTick]);

  const totalAlertCount = (spikes?.alerts?.length || 0) + (spikes?.product_alerts?.length || 0);

  const dispatchAlerts = async () => {
    if (!totalAlertCount) return;
    if (!window.confirm(`Dispatch ${totalAlertCount} alert(s) to engineering recipients (email + push)?`)) return;
    setDispatching(true);
    try {
      const r = await api.post("/warranty/insights/spikes/dispatch");
      const cDisp = r.data?.cluster?.alerts_dispatched ?? 0;
      const pDisp = r.data?.product?.alerts_dispatched ?? 0;
      const cSkip = (r.data?.cluster?.skipped || []).length;
      const pSkip = (r.data?.product?.skipped || []).length;
      window.alert(
        `Dispatched ${cDisp + pDisp} (${cDisp} cluster, ${pDisp} product), ` +
        `skipped ${cSkip + pSkip} (in cooldown).`
      );
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(extractErr(e, "Dispatch failed").message);
    } finally {
      setDispatching(false);
    }
  };

  const windows = data?.windows || [];
  const rows = useMemo(() => {
    const list = data?.[dim] || [];
    if (!search.trim()) return list;
    const s = search.trim().toLowerCase();
    return list.filter((r) => (r.value || "").toLowerCase().includes(s));
  }, [data, dim, search]);

  const winLabels = windows.map((w) => w.key.toUpperCase());
  const dimLabel = DIMENSIONS.find((d) => d.key === dim)?.label || "";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/warranty")} className="text-slate-500 hover:text-blue-700"
                    data-testid="insights-back">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <ChartLineUp size={20} weight="fill" className="text-violet-600" />
            <h1 className="text-base font-bold font-['Chivo']">{t("wi.title", "Warranty Insights")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="insights-recluster"
              onClick={async () => {
                if (!window.confirm("Re-cluster all cause strings via Claude? This may take 5-15 seconds.")) return;
                try {
                  const r = await api.post("/warranty/insights/recluster");
                  window.alert(`Reclustered ${r.data?.reclustered_count} causes (engine: ${r.data?.engine}).`);
                  setRefreshTick((t) => t + 1);
                } catch (e) {
                  toast.error(extractErr(e, "Recluster failed").message);
                }
              }}
              className="text-xs font-bold uppercase tracking-wider text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 px-2 py-1"
              title={t("wi.recluster_tip", "Force LLM re-classification of all cause strings")}
            >
              <Stack size={13} weight="bold" />
              Recluster
            </button>
            <button
              data-testid="insights-refresh"
              onClick={() => setRefreshTick((t) => t + 1)}
              disabled={loading}
              className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-blue-700 inline-flex items-center gap-1 px-2 py-1 disabled:opacity-40"
            >
              <ArrowsClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {loading && (
          <div className="text-center text-slate-400 p-12">
            <Spinner className="animate-spin inline mr-2" /> Loading insights…
          </div>
        )}

        {!loading && data && !data.error && (
          <>
            {/* Active spike alerts (cluster + product) */}
            {totalAlertCount > 0 && (
              <div data-testid="insights-spikes-banner" className="bg-rose-50 border-l-4 border-rose-500 rounded-sm p-5 mb-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Siren size={28} weight="fill" className="text-rose-600 shrink-0 mt-0.5 animate-pulse" />
                    <div>
                      <h3 className="font-black text-rose-900 mb-1">
                        {totalAlertCount} {t("wi.active_spike_alert", "active spike alert")}{totalAlertCount === 1 ? "" : "s"}
                        {spikes.product_alerts?.length > 0 && (
                          <span className="ml-2 text-xs font-bold uppercase tracking-wider text-violet-700 bg-violet-100 border border-violet-300 px-2 py-0.5 rounded-sm">
                            {spikes.alerts?.length || 0} cluster · {spikes.product_alerts.length} product
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-rose-800">
                        {t("wi.spike_explanation", "Cluster(s) with ≥{ratio}× their 90-day baseline rate (and ≥{min} claim(s) in 30d). Product-scoped uses an 11-month baseline.", { ratio: spikes.settings?.threshold_x ?? 3, min: spikes.settings?.min_recent ?? 3 })}
                      </p>
                    </div>
                  </div>
                  <button
                    data-testid="insights-spikes-dispatch"
                    onClick={dispatchAlerts}
                    disabled={dispatching}
                    className="px-4 py-2 bg-rose-600 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-rose-700 disabled:opacity-40 inline-flex items-center gap-2 shrink-0"
                  >
                    {dispatching ? <Spinner className="animate-spin" size={13} /> : <PaperPlaneTilt size={13} weight="bold" />}
                    Dispatch
                  </button>
                </div>

                {spikes.alerts?.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase font-bold tracking-wider text-rose-900 mt-4 mb-2">{t("wi.cluster_wide_spikes", "Cluster-wide spikes")}</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {spikes.alerts.map((a) => (
                        <div key={a.cluster_key} data-testid={`insights-spike-${a.cluster_key}`}
                             className="bg-white border border-rose-200 rounded-sm p-3 text-xs">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="font-bold text-slate-900 truncate">{a.cluster_key.replace(/_/g, " ")}</span>
                            <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-sm ${
                              a.is_emerging ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"
                            }`}>
                              {a.is_emerging ? "Emerging" : `${a.ratio}× spike`}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Recent 30d</div>
                              <div className="text-2xl font-black text-rose-700">{a.recent_count_30d}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Baseline 90d</div>
                              <div className="text-2xl font-black text-slate-500">{a.baseline_count_90d}</div>
                            </div>
                          </div>
                          <div className="text-[10px] text-slate-500 space-y-0.5">
                            {a.top_products?.length > 0 && (
                              <div><b className="text-slate-700">Products:</b> {a.top_products.slice(0,3).join(" · ")}</div>
                            )}
                            {a.top_suppliers?.length > 0 && (
                              <div><b className="text-slate-700">Suppliers:</b> {a.top_suppliers.slice(0,3).join(" · ")}</div>
                            )}
                            {a.top_dealers?.length > 0 && (
                              <div><b className="text-slate-700">Dealers:</b> {a.top_dealers.slice(0,3).join(" · ")}</div>
                            )}
                            {a.tickets?.length > 0 && (
                              <div className="font-mono"><b className="text-slate-700">Tickets:</b> {a.tickets.slice(0,4).join(" · ")}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {spikes.product_alerts?.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase font-bold tracking-wider text-violet-900 mt-4 mb-2">
                      {t("wi.per_product_spikes", "Per-product spikes")}
                      <span className="ml-2 normal-case font-normal text-[10px] text-slate-500 tracking-normal">
                        {t("wi.per_product_hint", "— catches manufacturing-batch / supplier-change issues isolated to one SKU")}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {spikes.product_alerts.map((a) => {
                        const key = `${a.product_model}::${a.cluster_key}`;
                        return (
                          <div key={key} data-testid={`insights-product-spike-${key}`}
                               className={`bg-white border rounded-sm p-3 text-xs ${
                                 a.is_product_specific ? "border-amber-300 ring-1 ring-amber-200" : "border-violet-200"
                               }`}>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <div className="font-black text-slate-900 truncate">{a.product_model}</div>
                                <div className="text-[10px] text-slate-500 truncate">{a.cluster_key.replace(/_/g, " ")}</div>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-sm ${
                                  a.is_emerging ? "bg-amber-100 text-amber-800" : "bg-violet-100 text-violet-700"
                                }`}>
                                  {a.is_emerging ? "Emerging" : `${a.ratio}× spike`}
                                </span>
                                {a.is_product_specific && (
                                  <span className="text-[9px] uppercase font-bold tracking-wider bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5 rounded-sm" title={`Global cluster ratio only ${a.global_ratio}× — looks SKU-specific`}>
                                    {t("wi.isolated_global", "Isolated · global")} {a.global_ratio}×
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div>
                                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Recent 30d</div>
                                <div className="text-2xl font-black text-violet-700">{a.recent_count_30d}</div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Baseline 12m</div>
                                <div className="text-2xl font-black text-slate-500">{a.baseline_count_12m}</div>
                              </div>
                            </div>
                            <div className="text-[10px] text-slate-500 space-y-0.5">
                              {a.top_suppliers?.length > 0 && (
                                <div><b className="text-slate-700">Suppliers:</b> {a.top_suppliers.slice(0,3).join(" · ")}</div>
                              )}
                              {a.top_dealers?.length > 0 && (
                                <div><b className="text-slate-700">Dealers:</b> {a.top_dealers.slice(0,3).join(" · ")}</div>
                              )}
                              {a.top_customers?.length > 0 && (
                                <div><b className="text-slate-700">Customers:</b> {a.top_customers.slice(0,3).join(" · ")}</div>
                              )}
                              {a.tickets?.length > 0 && (
                                <div className="font-mono"><b className="text-slate-700">Tickets:</b> {a.tickets.slice(0,4).join(" · ")}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Window summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="insights-windows">
              {windows.map((w, idx) => {
                const prior = windows[idx + 1];
                const delta = (prior && prior.total) ? Math.round(((w.total - prior.total) / prior.total) * 100) : null;
                return (
                  <div
                    key={w.key}
                    data-testid={`insights-window-${w.key}`}
                    className="bg-white border border-slate-200 rounded-sm p-4"
                  >
                    <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">{w.label}</div>
                    <div className="text-3xl font-black font-['Chivo'] text-slate-900 mt-1">{w.total}</div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center justify-between">
                      <span><Lightning size={10} weight="fill" className="inline text-rose-500" /> {w.emergency_count} emerg.</span>
                      <span>{w.emergency_rate_pct}%</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Avg <Package size={10} weight="bold" className="inline mr-0.5" /> missing PM: {w.missing_pm_avg}
                    </div>
                    {delta !== null && (
                      <div className="mt-2 text-[10px] flex items-center gap-1 font-bold uppercase tracking-wider">
                        {delta > 0 ? (
                          <span className="text-rose-700"><ArrowUp size={10} weight="bold" className="inline" /> {delta}% vs {prior.label.replace(/Last\s+/, "")}</span>
                        ) : delta < 0 ? (
                          <span className="text-emerald-700"><ArrowDown size={10} weight="bold" className="inline" /> {Math.abs(delta)}% vs {prior.label.replace(/Last\s+/, "")}</span>
                        ) : (
                          <span className="text-slate-500"><Minus size={10} weight="bold" className="inline" /> flat</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Dimension tabs */}
            <div className="flex flex-wrap gap-1 mb-3 border-b border-slate-200">
              {DIMENSIONS.map(({ key, label, icon: Icon, hint }) => (
                <button
                  key={key}
                  data-testid={`insights-tab-${key}`}
                  onClick={() => setDim(key)}
                  title={hint}
                  className={`text-xs font-bold uppercase tracking-wider px-3 py-2 border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                    dim === key ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <Icon size={12} weight="bold" />
                  {label}
                  <span className="ml-1 text-[10px] text-slate-400">{(data[key] || []).length}</span>
                </button>
              ))}
            </div>

            {/* Search + table */}
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="p-3 border-b border-slate-100 flex items-center gap-2">
                <MagnifyingGlass size={14} weight="bold" className="text-slate-400" />
                <input
                  data-testid="insights-search"
                  type="search"
                  placeholder={`Search ${dimLabel.toLowerCase()}…`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="text-sm flex-1 focus:outline-none"
                />
                <span className="text-xs text-slate-400">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                  <tr>
                    <th className="text-left p-3">{dimLabel}</th>
                    {winLabels.map((l) => (
                      <th key={l} className="text-right p-3 w-16">{l}</th>
                    ))}
                    <th className="text-right p-3 w-24" title={t("wi.vs_prior_tip", "vs prior 1-month period")}><span className="inline-flex items-center gap-1">Δ 1M <Info size={10} weight="bold" /></span></th>
                    <th className="text-right p-3 w-16">{t("table.emergency_short", "Emerg.")}</th>
                    <th className="text-right p-3 w-16">{t("table.pm_miss", "PM miss")}</th>
                    <th className="text-left p-3">{t("wi.top_ai_causes", "Top AI causes")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.length === 0 && (
                    <tr><td colSpan={4 + windows.length} className="p-6 text-center text-xs text-slate-400">{t("wi.no_rows", "No rows.")}</td></tr>
                  )}
                  {rows.map((r, i) => (
                    <tr key={`${dim}-${r.key}-${i}`} className="hover:bg-slate-50" data-testid={`insights-row-${i}`}>
                      <td className="p-3">
                        <div className="font-bold truncate max-w-[260px]" title={r.value}>{r.value}</div>
                        {r.key && r.key !== r.value && <div className="text-[10px] text-slate-400 font-mono">{r.key}</div>}
                      </td>
                      {(r.in_windows || []).map((n, wi) => (
                        <td key={wi} className={`text-right p-3 font-mono ${wi === 0 ? "font-bold" : "text-slate-600"}`}>{n}</td>
                      ))}
                      <td className="text-right p-3">
                        <DeltaBadge pct={r.delta_vs_prior_1m_pct} />
                      </td>
                      <td className="text-right p-3 text-rose-600 font-mono">
                        {r.emergency_count > 0 ? r.emergency_count : <span className="text-slate-300">·</span>}
                      </td>
                      <td className="text-right p-3 font-mono text-slate-600">
                        {r.missing_pm_total > 0 ? r.missing_pm_total : <span className="text-slate-300">·</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1 max-w-[420px]">
                          {(r.top_ai_causes || []).slice(0, 3).map((c, ci) => (
                            <span key={ci} className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200 rounded-sm px-1.5 py-0.5">
                              {c}
                            </span>
                          ))}
                          {(r.top_ai_causes || []).length === 0 && <span className="text-[10px] text-slate-300">—</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Recurring causes */}
            <h2 className="text-base font-black font-['Chivo'] mt-8 mb-2 flex items-center gap-2">
              <ChartBar size={16} weight="fill" className="text-amber-600" />
              Recurring root causes
              {data.cluster_engine && (
                <span
                  data-testid="insights-cluster-engine"
                  className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                    data.cluster_engine.startsWith("claude")
                      ? "bg-violet-100 text-violet-700 border border-violet-300"
                      : "bg-slate-100 text-slate-500 border border-slate-300"
                  }`}
                  title={`Cluster engine: ${data.cluster_engine}. Cached mappings: ${data.unique_causes_clustered ?? 0}.`}
                >
                  {data.cluster_engine}
                </span>
              )}
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              {t("wi.synonyms_intro", "Synonymous wording is collapsed by Claude Sonnet into stable taxonomy keys (e.g. \"airend bearing failure\" + \"input shaft bearing seized\" →")} <code>airend_bearing_failure</code>). {t("wi.clusters_surface", "Clusters with ≥2 hits surface here. Use this to spot systemic issues across product lines / suppliers.")}
            </p>
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="insights-recurring-table">
                <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                  <tr>
                    <th className="text-left p-3">{t("table.cluster", "Cluster")}</th>
                    <th className="text-right p-3 w-16">{t("table.count", "Count")}</th>
                    <th className="text-left p-3">{t("wi.top_products", "Top products")}</th>
                    <th className="text-left p-3">{t("wi.top_suppliers", "Top suppliers")}</th>
                    <th className="text-left p-3">{t("table.tickets", "Tickets")}</th>
                    <th className="text-left p-3">{t("wi.last_seen", "Last seen")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.recurring_causes || []).length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-xs text-slate-400">{t("wi.no_recurring", "No recurring patterns yet — need at least 2 matching claims to cluster.")}</td></tr>
                  )}
                  {(data.recurring_causes || []).map((c, i) => (
                    <tr key={i} className="hover:bg-slate-50" data-testid={`insights-cause-${i}`}>
                      <td className="p-3">
                        <div className="font-bold">{c.cluster_key}</div>
                        {c.examples?.[0] && (
                          <div className="text-[11px] text-slate-500 italic mt-0.5">"{c.examples[0]}"</div>
                        )}
                      </td>
                      <td className="text-right p-3 text-2xl font-black text-amber-700">{c.count}</td>
                      <td className="p-3 text-xs">
                        {(c.top_products || []).slice(0, 3).map((p, pi) => (
                          <div key={pi} className="truncate" title={p}>• {p}</div>
                        ))}
                      </td>
                      <td className="p-3 text-xs">
                        {(c.top_suppliers || []).slice(0, 3).map((s, si) => (
                          <div key={si} className="truncate" title={s}>• {s}</div>
                        ))}
                      </td>
                      <td className="p-3 text-xs font-mono space-y-0.5">
                        {(c.tickets || []).slice(0, 5).map((t, ti) => (
                          <div key={ti}>{t}</div>
                        ))}
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {c.last_seen ? new Date(c.last_seen).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function DeltaBadge({ pct }) {
  if (pct === undefined || pct === null) return <span className="text-slate-300">·</span>;
  const num = Number(pct);
  if (num === 0) return <span className="text-slate-400 text-xs"><Minus size={10} weight="bold" className="inline" /> 0%</span>;
  if (num > 0) return <span className="text-rose-700 font-bold text-xs"><ArrowUp size={10} weight="bold" className="inline" /> {num}%</span>;
  return <span className="text-emerald-700 font-bold text-xs"><ArrowDown size={10} weight="bold" className="inline" /> {Math.abs(num)}%</span>;
}
